import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * Utility helper to resolve nested object path values (e.g. 'currentPosition.status')
 */
function resolvePath(obj, path) {
    if (!path) return undefined;
    const keys = path.split('.');
    let current = obj;
    for (const key of keys) {
        if (current === null || current === undefined) return undefined;
        current = current[key];
    }
    return current;
}

/**
 * Helper to recursively flatten a nested object into a single-level object with dotted keys.
 * This ensures no data is hidden/omitted.
 */
function flattenObject(obj, prefix = '', res = {}) {
    if (!obj) return res;
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            const propName = prefix ? `${prefix}.${key}` : key;
            const val = obj[key];
            if (
                val &&
                typeof val === 'object' &&
                !Array.isArray(val) &&
                !(val instanceof Date) &&
                typeof val.toDate !== 'function'
            ) {
                flattenObject(val, propName, res);
            } else {
                res[propName] = val;
            }
        }
    }
    return res;
}

/**
 * Standard content formatting for cells in export documents
 */
function formatVal(val) {
    if (val === undefined || val === null || val === '') return '—';
    if (val instanceof Date) return val.toLocaleDateString();

    // Firestore Timestamp mock check
    if (val && typeof val.toDate === 'function') {
        try {
            return val.toDate().toLocaleDateString();
        } catch {
            // fallback
        }
    }

    if (typeof val === 'object') {
        return val.name || val.displayName || val.label || JSON.stringify(val);
    }
    return String(val);
}

/**
 * Exports data objects list to Excel file and auto-fits column widths
 */
export function exportToExcel(data, columns, filename = 'exported-report.xlsx') {
    const transformed = data.map(item => {
        const row = {};

        // 1. First add the mapped columns in order
        columns.forEach(col => {
            if (col.title && col.dataIndex) {
                const rawVal = resolvePath(item, col.dataIndex);
                row[col.title] = formatVal(rawVal);
            }
        });

        // 2. Flatten the item and add any remaining unmapped properties
        const flatObj = flattenObject(item);
        Object.entries(flatObj).forEach(([key, val]) => {
            const alreadyMapped = columns.some(col =>
                col.dataIndex === key || key.startsWith(col.dataIndex + '.')
            );
            if (!alreadyMapped) {
                const title = key
                    .split('.')
                    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
                    .join(' ')
                    .replace(/([A-Z])/g, ' $1')
                    .replace(/\s+/g, ' ')
                    .trim();
                row[title] = formatVal(val);
            }
        });

        return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(transformed);

    // Auto-fit column widths
    const maxLengths = {};
    transformed.forEach(row => {
        Object.keys(row).forEach(key => {
            const val = row[key];
            const cellLen = val ? String(val).length : 5;
            const headerLen = key ? String(key).length : 5;
            const len = Math.max(cellLen, headerLen);
            if (!maxLengths[key] || len > maxLengths[key]) {
                maxLengths[key] = len;
            }
        });
    });
    const cols = Object.keys(maxLengths).map(key => ({
        wch: Math.min(Math.max(maxLengths[key], 12), 48)
    }));
    worksheet['!cols'] = cols;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');
    XLSX.writeFile(workbook, filename);
}

/**
 * Exports data objects list to PDF document with EDULINK styles
 */
export function exportToPdf(data, columns, title = 'EDULINK Report', filename = 'exported-report.pdf') {
    const doc = new jsPDF({
        orientation: 'l', // landscape for wider tables
        unit: 'pt',
        format: 'a4',
    });

    // Brand header
    doc.setFontSize(20);
    doc.setTextColor(240, 74, 74); // EDULINK Red primary
    doc.text('EDULINK', 40, 45);

    doc.setFontSize(14);
    doc.setTextColor(60, 60, 60);
    doc.text(title, 40, 65);

    doc.setFontSize(9);
    doc.setTextColor(120, 120, 120);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 40, 80);

    const head = [columns.map(col => col.title)];
    const body = data.map(item => {
        return columns.map(col => {
            const rawVal = resolvePath(item, col.dataIndex);
            return formatVal(rawVal);
        });
    });

    autoTable(doc, {
        head: head,
        body: body,
        startY: 95,
        styles: { fontSize: 8, cellPadding: 6 },
        headStyles: { fillColor: [240, 74, 74] },
        theme: 'striped',
    });

    doc.save(filename);
}
